import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { Account, AccountSubscriptionStatus } from '../api/client';
import { useAuth } from './AuthContext';
import { useQuietRefresh } from '../hooks/useQuietRefresh';

/** 'error' = the statuses could not be read; plan pills are hidden rather than
 *  every account reading as "No plan". */
export type SubscriptionsState = 'loading' | 'ok' | 'error';

interface AccountsContextType {
  /** The console's Accounts for this login, oldest first (as the API returns them). */
  accounts: Account[];
  /** False until the first load for this login has finished (or failed). */
  loaded: boolean;
  subscriptionMap: Record<string, AccountSubscriptionStatus>;
  subscriptionsState: SubscriptionsState;
  /** Re-read accounts and plan statuses without a loading state. */
  refresh: () => Promise<void>;
  /** Re-read only the plan statuses (after a plan change or checkout). */
  refreshSubscriptions: () => Promise<void>;
  /** Merge a change made in this tab (AI toggle, a saved account) into the list. */
  updateAccount: (accountId: string, patch: Partial<Account>) => void;
  removeAccount: (accountId: string) => void;
}

const AccountsContext = createContext<AccountsContextType | undefined>(undefined);

// A busy backend answers 503 now and then (a stalled read is "try again", never an
// empty list); the first load retries so a login during a stall still gets its list.
// Later quiet refreshes don't need to: a failed one keeps the list already shown.
const FIRST_LOAD_ATTEMPTS = 3;
const FIRST_LOAD_RETRY_DELAY_MS = 500;

/**
 * The Accounts list (see CONTEXT.md) — loaded once per login and shared by the
 * shell's sidebar and the Dashboard, so moving between pages never refetches it.
 * Changes made in this tab are patched in; everything else (a teammate's toggle,
 * a finished connection, a paused wallet) arrives with the quiet refresh.
 */
export function AccountsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const userId = isAuthenticated ? user?.id ?? null : null;

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [subscriptionMap, setSubscriptionMap] = useState<Record<string, AccountSubscriptionStatus>>({});
  const [subscriptionsState, setSubscriptionsState] = useState<SubscriptionsState>('loading');

  // Bumped on logout/login switch: responses for an older login are dropped.
  const session = useRef(0);
  // Bumped on every local patch: a refresh that started before it would put back
  // the pre-patch row, so its result is dropped (the next refresh catches up).
  const edits = useRef(0);

  const loadAccounts = useCallback(async () => {
    const s = session.current;
    const e = edits.current;
    const rows = await api.getAccounts();
    if (s === session.current && e === edits.current) setAccounts(rows);
  }, []);

  const loadSubscriptions = useCallback(async (quiet: boolean) => {
    const s = session.current;
    try {
      const map = await api.getAccountsSubscriptionStatus();
      if (s !== session.current) return;
      setSubscriptionMap(map);
      setSubscriptionsState('ok');
    } catch {
      // A failed background read keeps the statuses already shown.
      if (s === session.current && !quiet) setSubscriptionsState('error');
    }
  }, []);

  useEffect(() => {
    session.current += 1;
    setAccounts([]);
    setSubscriptionMap({});
    setSubscriptionsState('loading');
    setLoaded(!userId);
    if (!userId) return;
    const s = session.current;
    void (async () => {
      for (let attempt = 0; attempt < FIRST_LOAD_ATTEMPTS && s === session.current; attempt++) {
        try {
          await loadAccounts();
          break;
        } catch (error) {
          if (attempt + 1 === FIRST_LOAD_ATTEMPTS) {
            console.error('Failed to fetch accounts:', error);
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, FIRST_LOAD_RETRY_DELAY_MS * (attempt + 1)));
        }
      }
      if (s === session.current) setLoaded(true);
    })();
    void loadSubscriptions(false);
  }, [userId, loadAccounts, loadSubscriptions]);

  const refreshSubscriptions = useCallback(() => loadSubscriptions(true), [loadSubscriptions]);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadAccounts().catch(() => {}),
      loadSubscriptions(true),
    ]);
  }, [loadAccounts, loadSubscriptions]);

  useQuietRefresh(refresh, Boolean(userId) && loaded);

  const updateAccount = useCallback((accountId: string, patch: Partial<Account>) => {
    edits.current += 1;
    setAccounts((prev) => prev.map((a) => (a.account_id === accountId ? { ...a, ...patch } : a)));
  }, []);

  const removeAccount = useCallback((accountId: string) => {
    edits.current += 1;
    setAccounts((prev) => prev.filter((a) => a.account_id !== accountId));
  }, []);

  const value = useMemo(
    () => ({ accounts, loaded, subscriptionMap, subscriptionsState, refresh, refreshSubscriptions, updateAccount, removeAccount }),
    [accounts, loaded, subscriptionMap, subscriptionsState, refresh, refreshSubscriptions, updateAccount, removeAccount],
  );

  return <AccountsContext.Provider value={value}>{children}</AccountsContext.Provider>;
}

export function useAccounts() {
  const context = useContext(AccountsContext);
  if (context === undefined) {
    throw new Error('useAccounts must be used within an AccountsProvider');
  }
  return context;
}
